var _IconSettingsSolid;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import { InstUISettingsProvider } from '@instructure/emotion';
import { Drilldown } from '@instructure/ui-drilldown';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { Responsive } from '@instructure/ui-responsive';
import { IconSettingsSolid } from '@instructure/ui-icons';
import { Checkbox } from '@instructure/ui-checkbox';
import { View } from '@instructure/ui-view';
import { translate } from '../../../constants/translated/translations';
import { captionColors, captionPositions } from '../../../constants';
import PlayerButton from '../../PlayerButton';
import MediaContext from '../../Player/MediaContext';
const licorice = '#2D3B45';
const prefix = 'MediaPlayer';
const DrilldownPage = {
  VideoSettings: `${prefix}VideoSettingsPageId`,
  Captions: `${prefix}CaptionsPageId`,
  Speed: `${prefix}SpeedPageId`,
  Quality: `${prefix}QualityPageId`,
  Language: `${prefix}LanguagePageId`,
  CaptionSize: `${prefix}CaptionSizePageId`
};
const DrilldownOption = {
  Captions: `${prefix}CaptionsOptionId`,
  Speed: `${prefix}SpeedOptionId`,
  Quality: `${prefix}QualityOptionId`,
  Language: `${prefix}LanguageOptionId`,
  CaptionPositionToggle: `${prefix}CaptionPositionToggleId`,
  CaptionInvertColorToggle: `${prefix}CaptionInvertColorToggleId`,
  CaptionSize: `${prefix}CaptionSizeOptionId`
};
const DrilldownGroup = {
  Language: `${prefix}LanguageGroup`,
  Speed: `${prefix}SpeedGroup`,
  Quality: `${prefix}QualityGroup`,
  CaptionSize: `${prefix}CaptionSizeGroup`
};

/**
---
private: true
---
**/
class PlayerSettings extends Component {
  constructor(props) {
    super(props);
    this.fontSizeLabels = {
      normal: translate('NORMAL'),
      large: translate('LARGE'),
      extra: translate('X_LARGE')
    };
  }
  renderSelected(props, value) {
    return /*#__PURE__*/React.createElement("span", {
      style: {
        color: props.variant === 'default' ? 'white' : void 0
      }
    }, value);
  }
  renderSelectGroup(options, name, selectedValue, onClick, mapOptionValue) {
    let optionsMap = options.map((option, idx) => {
      const optionValue = mapOptionValue ? mapOptionValue(option) : option;
      return /*#__PURE__*/React.createElement(Drilldown.Option, {
        key: idx,
        id: `${idx}`,
        value: option,
        selected: option === selectedValue,
        onOptionClick: () => {
          onClick && onClick(option);
        },
        "data-test": `${name}-${optionValue === null || optionValue === void 0 ? void 0 : optionValue.replace(' ', '-')}`
      }, optionValue);
    });
    if (name === DrilldownGroup.Language) {
      optionsMap.unshift( /*#__PURE__*/React.createElement(Drilldown.Option, {
        key: "off",
        id: "caption-off",
        value: "off",
        selected: selectedValue === void 0,
        onOptionClick: () => {
          onClick && onClick(void 0);
        },
        "data-test": "MediaPlayerCaptionOff"
      }, translate('OFF')));
    }
    return /*#__PURE__*/React.createElement(Drilldown.Group, {
      id: name,
      withoutSeparators: true
    }, optionsMap);
  }
  renderTrigger({
    videoId
  }) {
    return /*#__PURE__*/React.createElement(PlayerButton, {
      videoId: videoId,
      tooltipLabel: translate('SETTINGS')
    }, /*#__PURE__*/React.createElement(ScreenReaderContent, null, translate('SETTINGS')), _IconSettingsSolid || (_IconSettingsSolid = /*#__PURE__*/React.createElement(IconSettingsSolid, {
      size: "x-small"
    })));
  }
  renderToggle({
    id,
    name,
    label,
    onClick,
    checked
  }) {
    return /*#__PURE__*/React.createElement(Drilldown.Option, {
      id: id,
      key: id,
      themeOverride: {
        padding: '0.25rem 0.75rem'
      },
      afterLabelContentVAlign: "center",
      onOptionClick: () => {
        onClick();
      },
      role: "checkbox",
      "aria-checked": checked ? 'true' : 'false'
      // prevents reading the label of the checkbox too (duplicated)
      // eslint-disable-next-line jsx-a11y/aria-proptypes
      ,
      "aria-describedby": [''],
      renderLabelInfo: /*#__PURE__*/React.createElement(Checkbox, {
        label: /*#__PURE__*/React.createElement(ScreenReaderContent, null, label),
        variant: "toggle",
        readOnly: true,
        checked: checked,
        onChange: () => {
          // needed for controlled Checkbox,
          // but the state is handled on the Drilldown.Option
        },
        labelPlacement: "start",
        tabIndex: -1
      }),
      "data-test": `MediaPlayerSettingsToggle-${id}`
    }, name);
  }
  renderCaptionsPage({
    tracks,
    selectedTrackId,
    captionPosition,
    captionColor,
    captionFontSize
  }, {
    toggleCaptionPosition,
    toggleCaptionColor
  }) {
    const onCaptionPositionToggle = () => {
      toggleCaptionPosition();
    };
    const onCaptionColorToggle = () => {
      toggleCaptionColor();
    };
    return /*#__PURE__*/React.createElement(Drilldown.Page, {
      id: DrilldownPage.Captions,
      renderTitle: translate('CAPTIONS'),
      renderBackButtonLabel: translate('BACK'),
      withoutHeaderSeparator: true
    }, /*#__PURE__*/React.createElement(Drilldown.Option, {
      id: DrilldownOption.Language,
      subPageId: DrilldownPage.Language,
      renderLabelInfo: props => {
        const selectedTrack = tracks.find(t => t.id === selectedTrackId);
        return this.renderSelected(props, (selectedTrack === null || selectedTrack === void 0 ? void 0 : selectedTrack.label) || translate('OFF'));
      },
      "data-test": DrilldownOption.Language
    }, translate('LANGUAGE')), /*#__PURE__*/React.createElement(Drilldown.Option, {
      id: DrilldownOption.CaptionSize,
      subPageId: DrilldownPage.CaptionSize,
      renderLabelInfo: props => {
        return this.renderSelected(props, this.fontSizeLabels[captionFontSize]);
      },
      "data-test": DrilldownOption.CaptionSize
    }, translate('SIZE')), this.renderToggle({
      id: DrilldownOption.CaptionPositionToggle,
      name: translate('ON_TOP'),
      label: captionPosition === captionPositions.TOP ? translate('PLACE_CAPTION_TO_BOTTOM') : translate('PLACE_CAPTION_TO_TOP'),
      onClick: onCaptionPositionToggle,
      checked: captionPosition === captionPositions.TOP
    }), this.renderToggle({
      id: DrilldownOption.CaptionInvertColorToggle,
      name: translate('INVERT_COLORS'),
      label: captionColor === captionColors.DARK ? translate('CHANGE_CAPTION_COLOR_TO_LIGHT_THEME') : translate('CHANGE_CAPTION_COLOR_TO_DARK_THEME'),
      onClick: onCaptionColorToggle,
      checked: captionColor === captionColors.LIGHT
    }));
  }
  renderLanguagePage({
    tracks,
    selectedTrackId
  }, {
    toggleTrack
  }) {
    var _tracks$find;
    return /*#__PURE__*/React.createElement(Drilldown.Page, {
      id: DrilldownPage.Language,
      renderTitle: translate('LANGUAGE'),
      renderBackButtonLabel: translate('BACK'),
      withoutHeaderSeparator: true
    }, this.renderSelectGroup(tracks.map(t => t.label), DrilldownGroup.Language, (_tracks$find = tracks.find(t => t.id === selectedTrackId)) === null || _tracks$find === void 0 ? void 0 : _tracks$find.label, track => {
      const selectedTrack = tracks.find(t => t.label === track);
      toggleTrack(selectedTrack === null || selectedTrack === void 0 ? void 0 : selectedTrack.id);
    }));
  }
  renderSizePage({
    captionFontSize
  }, {
    setCaptionFontSize
  }) {
    return /*#__PURE__*/React.createElement(Drilldown.Page, {
      id: DrilldownPage.CaptionSize,
      renderTitle: translate('SIZE'),
      renderBackButtonLabel: translate('BACK'),
      withoutHeaderSeparator: true
    }, this.renderSelectGroup(Object.values(this.fontSizeLabels), DrilldownGroup.CaptionSize, this.fontSizeLabels[captionFontSize], size => {
      const selectedSize = Object.keys(this.fontSizeLabels).find(key => this.fontSizeLabels[key] === size);
      setCaptionFontSize(selectedSize);
    }));
  }
  renderSpeedPage(options, {
    playbackSpeed
  }, {
    setPlaybackSpeed,
    savePlayerSettings
  }) {
    return /*#__PURE__*/React.createElement(Drilldown.Page, {
      id: DrilldownPage.Speed,
      renderTitle: translate('SPEED'),
      renderBackButtonLabel: translate('BACK'),
      withoutHeaderSeparator: true
    }, this.renderSelectGroup(options, DrilldownGroup.Speed, playbackSpeed, speed => {
      setPlaybackSpeed(speed);
      savePlayerSettings({
        playbackSpeed: speed
      });
    }, option => `${option}x`));
  }
  renderQualityPage({
    sources,
    selectedSrc
  }, {
    setSource
  }) {
    var _sources$find;
    return /*#__PURE__*/React.createElement(Drilldown.Page, {
      id: DrilldownPage.Quality,
      renderTitle: translate('QUALITY'),
      renderBackButtonLabel: translate('BACK'),
      withoutHeaderSeparator: true
    }, this.renderSelectGroup(sources.map(s => s.label || translate('STANDARD')), DrilldownGroup.Quality, (_sources$find = sources.find(s => s.src === selectedSrc)) === null || _sources$find === void 0 ? void 0 : _sources$find.label, source => {
      const selectedSource = sources.find(s => s.label === source);
      setSource(selectedSource.src);
    }));
  }
  renderCustomControls() {
    const customControls = this.props.customControls;
    if (!Array.isArray(customControls) || customControls.length === 0) {
      return null;
    }
    return customControls.map(({
      name,
      checked,
      onClick,
      label
    }, idx) => this.renderToggle({
      id: `control${idx}`,
      name,
      label,
      onClick,
      checked
    }));
  }
  render() {
    return /*#__PURE__*/React.createElement(MediaContext.Consumer, null, ({
      state,
      fullScreenContainerRef,
      actions,
      playbackSpeedOptions
    }) => {
      const isSourceChooserDisplayed = state.sources.length > 1;
      const isTrackChooserDisplayed = state.tracks && !!state.tracks.length;
      return /*#__PURE__*/React.createElement(Responsive, {
        match: "media",
        query: {
          small: {
            maxWidth: 480
          }
        }
      }, (_, matches) => /*#__PURE__*/React.createElement(InstUISettingsProvider, {
        theme: {
          componentOverrides: {
            'Options.Item': {
              background: licorice,
              color: '#FFF',
              highlightedBackground: '#FFF',
              highlightedLabelColor: licorice
            },
            View: {
              backgroundPrimary: licorice,
              borderColorPrimary: licorice
            }
          }
        }
      }, /*#__PURE__*/React.createElement(View, {
        onKeyDown: e => {
          if (this.isSettingsShown && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
            e.stopPropagation();
          }
        }
      }, /*#__PURE__*/React.createElement(Drilldown, {
        rootPageId: DrilldownPage.VideoSettings,
        mountNode: fullScreenContainerRef,
        constrain: "parent",
        withArrow: false,
        offsetX: -38,
        offsetY: 8,
        width: matches.includes('small') ? '16rem' : '18.5rem',
        maxHeight: "15.5rem",
        shouldHideOnSelect: false,
        trigger: this.renderTrigger(state),
        onToggle: (_event, {
          shown,
          goToPage
        }) => {
          this.isSettingsShown = shown;
          shown && goToPage(DrilldownPage.VideoSettings);
        },
        shouldContainFocus: true
      }, /*#__PURE__*/React.createElement(Drilldown.Page, {
        id: DrilldownPage.VideoSettings,
        renderTitle: translate('SETTINGS'),
        withoutHeaderSeparator: true
      }, isTrackChooserDisplayed && /*#__PURE__*/React.createElement(Drilldown.Option, {
        id: DrilldownOption.Captions,
        subPageId: DrilldownPage.Captions,
        renderLabelInfo: props => {
          const selectedTrack = state.tracks.find(t => t.id === state.selectedTrackId);
          return this.renderSelected(props, (selectedTrack === null || selectedTrack === void 0 ? void 0 : selectedTrack.label) || translate('OFF'));
        },
        "data-test": DrilldownOption.Captions
      }, translate('CAPTIONS')), /*#__PURE__*/React.createElement(Drilldown.Option, {
        id: DrilldownOption.Speed,
        subPageId: DrilldownPage.Speed,
        renderLabelInfo: props => this.renderSelected(props, `${state.playbackSpeed}x`),
        "data-test": DrilldownOption.Speed
      }, translate('SPEED')), isSourceChooserDisplayed && /*#__PURE__*/React.createElement(Drilldown.Option, {
        id: DrilldownOption.Quality,
        subPageId: DrilldownPage.Quality,
        renderLabelInfo: props => {
          const selectedSource = state.sources.find(s => s.src === state.selectedSrc);
          return this.renderSelected(props, (selectedSource === null || selectedSource === void 0 ? void 0 : selectedSource.label) || translate('STANDARD'));
        },
        "data-test": DrilldownOption.Quality
      }, translate('QUALITY')), this.renderCustomControls()), isTrackChooserDisplayed && this.renderCaptionsPage(state, actions), this.renderLanguagePage(state, actions), this.renderSizePage(state, actions), this.renderSpeedPage(playbackSpeedOptions, state, actions), isSourceChooserDisplayed && this.renderQualityPage(state, actions)))));
    });
  }
}
export default PlayerSettings;